import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, ArrowLeft, ArrowRight, Lock, MapPin } from "lucide-react";

const NAVY = "#16202b";
const YELLOW = "#FFD400";
const API = process.env.REACT_APP_BACKEND_URL;
const RECRUITING_PIXEL = "518300620850778";

// Recruiting-Pixel laeuft ausschliesslich auf dieser Landingpage (nach Cookie-Einwilligung).
export const initRecruitingPixel = () => {
  if (typeof window === "undefined") return false;
  if (window.__recruitingPixel) return true;
  try {
    if (localStorage.getItem("el-consent") !== "all") return false;
  } catch (e) {
    return false;
  }
  if (typeof window.__enablePixel === "function") window.__enablePixel();
  if (typeof window.fbq !== "function") return false;
  window.__recruitingPixel = true;
  window.fbq("init", RECRUITING_PIXEL);
  return true;
};

const fb = (event, params) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function" && window.__recruitingPixel) {
    window.fbq("trackSingle", RECRUITING_PIXEL, event, params);
  }
};

const fbCustom = (event, params) => {
  if (typeof window !== "undefined" && typeof window.fbq === "function" && window.__recruitingPixel) {
    window.fbq("trackSingleCustom", RECRUITING_PIXEL, event, params);
  }
};

const scrollToFunnel = () => {
  const el = document.getElementById("funnel");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Cta = ({ testId, label = "Jetzt bewerben", className = "" }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={scrollToFunnel}
    className={`inline-flex items-center justify-center gap-2.5 rounded-[6px] bg-[#FFD400] px-8 py-[18px] text-[15px] font-extrabold uppercase tracking-[0.04em] text-[#16202b] hover:bg-[#ffe34d] ${className}`}
    style={{ transition: "background-color .25s ease, transform .25s ease" }}
  >
    {label}
    <ArrowRight size={18} strokeWidth={2.6} />
  </button>
);

const VORTEILE = [
  "Warme Leads auf Abruf — Kunden, die schon angefragt haben",
  "Keine Kaltakquise, kein Klinkenputzen",
  "Kein Außendienst: Beratung per Telefon und Videocall",
  "Hybrides Modell — Büro in Brühl und Homeoffice",
  "Hohes, attraktives Fixum",
  "Ungedeckelte Provision — nach oben offen",
  "Unbefristeter Arbeitsvertrag, Festanstellung",
  "Lockere Firmenstrukturen, kurze Wege, viele Benefits",
];

const PROFIL = [
  "Du wohnst im Umfeld von Brühl, Köln oder Bonn — der Arbeitsort ist Brühl, gearbeitet wird hybrid",
  "Du bist ein Vertriebstalent und verkaufst gerne am Telefon und per Videocall",
  "Idealerweise bringst du Erfahrung im PV- oder Wärmepumpen-Vertrieb mit",
];

const STEPS = [
  {
    key: "jahre",
    q: "Wie viele Jahre Vertriebserfahrung bringst du mit?",
    options: ["Weniger als 1 Jahr", "1–3 Jahre", "3–5 Jahre", "Mehr als 5 Jahre"],
  },
  {
    key: "branche",
    q: "Hast du schon PV oder Wärmepumpen verkauft?",
    options: [
      "Ja, Photovoltaik",
      "Ja, Wärmepumpen",
      "Ja, beides",
      "Nein, aber anderen Vertrieb",
    ],
  },
  {
    key: "start",
    q: "Ab wann bist du einsatzbereit?",
    options: ["Sofort", "In 1 Monat", "In 2–3 Monaten", "Später / noch offen"],
  },
];

const inputCls =
  "w-full rounded-[6px] border-2 border-[#2c3b4b] bg-[#1d2936] px-4 py-3.5 text-[16px] text-white outline-none placeholder:text-white/35 focus:border-[#FFD400]";

const Funnel = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ vorname: "", nachname: "", email: "", phone: "", consent: false });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const choose = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    fbCustom("FunnelStep", { step: step + 1, question: key, answer: value });
    if (step === 0) fb("InitiateCheckout");
    setStep((s) => s + 1);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vorname || !form.nachname || !form.email || !form.phone) {
      setError("Bitte fülle alle Felder aus.");
      return;
    }
    if (!form.consent) {
      setError("Bitte bestätige die Einwilligung, damit wir dich kontaktieren dürfen.");
      return;
    }
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/api/sales-agent-lead`, {
        ...form,
        jahre_vertrieb: answers.jahre,
        branchen_erfahrung: answers.branche,
        einsatzbereit: answers.start,
        position: "Sales Agent (m/w/d) PV & Wärmepumpen — Arbeitsort Brühl",
      });
      // CompleteRegistration wird auf der Danke-Seite ausgeloest (verhindert Doppelzaehlung)
      window.location.href = "/lp/sales-agent-bruehl/danke/";
    } catch (err) {
      setError("Das hat leider nicht geklappt. Bitte versuche es gleich noch einmal.");
      setSending(false);
    }
  };

  const total = STEPS.length + 1;
  const current = STEPS[step];

  return (
    <section id="funnel" data-testid="sa-funnel" className="scroll-mt-4 bg-[#16202b] px-6 py-16 md:px-10 md:py-24">
      <div className="mx-auto max-w-[680px]">
        <div className="flex items-center gap-2" data-testid="sa-progress">
          {Array.from({ length: total }).map((_, i) => (
            <span
              key={i}
              className="h-[5px] flex-1 rounded-full"
              style={{ background: i <= step ? YELLOW : "#2c3b4b", transition: "background-color .3s ease" }}
            />
          ))}
        </div>
        <p className="mt-4 text-[13px] font-bold uppercase tracking-[0.16em] text-[#FFD400]">
          Schritt {Math.min(step + 1, total)} von {total}
        </p>

        {current ? (
          <div className="mt-7">
            <h2 className="text-[26px] font-extrabold leading-[1.22] text-white md:text-[32px]" data-testid="sa-question">
              {current.q}
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-3">
              {current.options.map((o) => (
                <button
                  key={o}
                  type="button"
                  data-testid={`sa-option-${current.key}-${o}`}
                  onClick={() => choose(current.key, o)}
                  className="group flex items-center justify-between rounded-[8px] border-2 border-[#2c3b4b] bg-[#1d2936] px-6 py-5 text-left text-[17px] font-semibold text-white hover:border-[#FFD400] hover:bg-[#223040]"
                  style={{ transition: "border-color .2s ease, background-color .2s ease" }}
                >
                  {o}
                  <ArrowRight size={18} className="text-[#FFD400] opacity-0 group-hover:opacity-100" style={{ transition: "opacity .2s ease" }} />
                </button>
              ))}
            </div>
            {step > 0 && (
              <button
                type="button"
                data-testid="sa-back"
                onClick={() => setStep((s) => s - 1)}
                className="mt-7 inline-flex items-center gap-2 text-[14px] font-semibold text-white/50 hover:text-white"
              >
                <ArrowLeft size={15} /> Zurück
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={submit} data-testid="sa-form" className="mt-7" noValidate>
            <h2 className="text-[26px] font-extrabold leading-[1.22] text-white md:text-[32px]">
              Fast geschafft — wohin dürfen wir uns melden?
            </h2>
            <p className="mt-4 text-[16px] leading-[1.7] text-white/60">
              Wir melden uns innerhalb von 24 Stunden telefonisch bei dir. Kein Lebenslauf nötig, kein Anschreiben.
            </p>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input data-testid="sa-vorname" className={inputCls} placeholder="Vorname" value={form.vorname} onChange={(e) => set("vorname", e.target.value)} autoComplete="given-name" />
              <input data-testid="sa-nachname" className={inputCls} placeholder="Nachname" value={form.nachname} onChange={(e) => set("nachname", e.target.value)} autoComplete="family-name" />
              <input data-testid="sa-email" type="email" className={inputCls} placeholder="E-Mail" value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" />
              <input data-testid="sa-rufnummer" type="tel" className={inputCls} placeholder="Rufnummer" value={form.phone} onChange={(e) => set("phone", e.target.value)} autoComplete="tel" />
            </div>

            <label className="mt-7 flex items-start gap-3 text-[14.5px] leading-[1.6] text-white/60">
              <input
                data-testid="sa-consent"
                type="checkbox"
                className="mt-[3px] h-[19px] w-[19px] shrink-0 accent-[#FFD400]"
                checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)}
              />
              <span>
                Ich willige ein, dass energyleads und Partnerunternehmen meine Daten zur Kontaktaufnahme wegen eines
                Jobangebots verarbeiten. Widerruf jederzeit möglich ·{" "}
                <a
                  href="/datenschutzerklaerung/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-white"
                >
                  Datenschutz
                </a>
              </span>
            </label>

            {error && (
              <p data-testid="sa-error" className="mt-5 rounded-[6px] bg-[#ff5a5a]/15 px-4 py-3 text-[14.5px] text-[#ff9b9b]">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="sa-submit"
              disabled={sending}
              className="mt-7 inline-flex w-full items-center justify-center gap-2.5 rounded-[6px] bg-[#FFD400] px-8 py-[19px] text-[16px] font-extrabold uppercase tracking-[0.04em] text-[#16202b] hover:bg-[#ffe34d] disabled:opacity-60"
              style={{ transition: "background-color .25s ease" }}
            >
              {sending ? "Wird gesendet …" : "Bewerbung abschicken"}
              {!sending && <ArrowRight size={18} strokeWidth={2.6} />}
            </button>
            <p className="mt-5 flex items-center justify-center gap-2 text-[13px] text-white/40">
              <Lock size={13} /> Deine Daten werden vertraulich behandelt
            </p>
            <button
              type="button"
              data-testid="sa-back-form"
              onClick={() => setStep((s) => s - 1)}
              className="mt-6 inline-flex items-center gap-2 text-[14px] font-semibold text-white/50 hover:text-white"
            >
              <ArrowLeft size={15} /> Zurück
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default function LandingSalesAgent() {
  useEffect(() => {
    document.title = "Sales Agent (m/w/d) PV & Wärmepumpen — Arbeitsort Brühl";
    const robots = document.querySelector('meta[name="robots"]');
    if (robots) robots.setAttribute("content", "noindex, nofollow");
    const start = () => {
      if (initRecruitingPixel()) fb("PageView");
    };
    start();
    window.addEventListener("el-consent", start);
    window.scrollTo(0, 0);
    return () => window.removeEventListener("el-consent", start);
  }, []);

  return (
    <div className="min-h-screen bg-[#16202b] text-white" data-testid="sa-landing">
      {/* Hero */}
      <section className="relative overflow-hidden border-b-4 border-[#FFD400] px-6 pb-14 pt-12 md:px-10 md:pb-20 md:pt-16">
        <div className="mx-auto grid max-w-[1120px] grid-cols-1 items-center gap-10 lg:grid-cols-[1.08fr_0.92fr] lg:gap-14">
          <div>
            <span
              className="inline-block rounded-[4px] px-3 py-1.5 text-[12.5px] font-extrabold uppercase tracking-[0.14em] text-[#16202b]"
              style={{ background: YELLOW }}
            >
              Wir suchen
            </span>
            <h1 className="mt-6 text-[38px] font-extrabold leading-[1.05] tracking-[-0.02em] md:text-[54px]" data-testid="sa-h1">
              Schluss mit <span className="text-[#FFD400]">Kaltakquise.</span>
            </h1>
            <p className="mt-6 max-w-[520px] text-[18px] leading-[1.55] text-white/80 md:text-[20px]">
              <strong className="font-bold text-white">Sales Agent (m/w/d)</strong> für Photovoltaik &amp; Wärmepumpen —
              du berätst Kunden, die bereits angefragt haben. Entspannt per Telefon und Videocall, kein Außendienst.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 text-[16px] font-semibold text-[#FFD400]">
              <MapPin size={17} /> Arbeitsort Brühl · hybrid mit Homeoffice · Kunden im Umkreis von 80 km
            </p>

            <div className="mt-9 grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { k: "Gehalt", v: "Attraktives Fixum" },
                { k: "On Top", v: "Provision ohne Deckel" },
                { k: "Vertrag", v: "Fest & unbefristet" },
              ].map((b) => (
                <div
                  key={b.k}
                  className="rounded-[8px] border border-white/12 border-l-[3px] border-l-[#FFD400] bg-white/[0.045] px-5 py-4"
                  data-testid={`sa-badge-${b.k}`}
                >
                  <p className="text-[11.5px] font-extrabold uppercase tracking-[0.16em] text-[#FFD400]">{b.k}</p>
                  <p className="mt-1.5 text-[17px] font-extrabold leading-[1.25] text-white">{b.v}</p>
                </div>
              ))}
            </div>

            <div className="mt-9">
              <Cta testId="sa-cta-hero" />
            </div>
            <p className="mt-5 text-[14px] text-white/45">Bewerbung in 60 Sekunden · kein Lebenslauf nötig</p>
          </div>

          <div className="relative w-full">
            <div className="img-frame aspect-[4/3] w-full">
              <img
                src="/img/hero-mann.webp"
                alt="Vertriebsmitarbeiter für Photovoltaik und Wärmepumpen"
                width="1264"
                height="948"
                fetchPriority="high"
                decoding="async"
                className="h-full w-full"
              />
              <span
                className="pointer-events-none absolute inset-0 rounded-[6px] border border-white/10"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vorteile */}
      <section className="px-6 py-16 md:px-10 md:py-20" data-testid="sa-vorteile">
        <div className="mx-auto max-w-[1120px]">
          <h2 className="text-[28px] font-extrabold leading-[1.2] md:text-[36px]">
            Das bekommst <span className="text-[#FFD400]">du</span>
          </h2>
          <ul className="mt-9 grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
            {VORTEILE.map((v) => (
              <li key={v} className="flex items-start gap-3.5 border-b border-white/10 pb-4 text-[16.5px] leading-[1.5] text-white/85">
                <span className="mt-[2px] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#FFD400]">
                  <Check size={13} strokeWidth={3.2} className="text-[#16202b]" />
                </span>
                {v}
              </li>
            ))}
          </ul>
          <div className="mt-10">
            <Cta testId="sa-cta-vorteile" />
          </div>
        </div>
      </section>

      {/* Profil */}
      <section className="bg-[#1d2936] px-6 py-16 md:px-10 md:py-20" data-testid="sa-profil">
        <div className="mx-auto max-w-[1120px]">
          <h2 className="text-[28px] font-extrabold leading-[1.2] md:text-[36px]">
            Passt <span className="text-[#FFD400]">du</span> zu uns?
          </h2>
          <div className="mt-9 grid grid-cols-1 gap-5 md:grid-cols-3">
            {PROFIL.map((p, i) => (
              <div key={p} className="rounded-[8px] border-l-4 border-[#FFD400] bg-[#16202b] px-6 py-6" data-testid={`sa-profil-${i}`}>
                <p className="font-display text-[14px] font-extrabold tracking-[0.14em] text-[#FFD400]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="mt-3 text-[16.5px] leading-[1.6] text-white/85">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Funnel />

      <footer className="border-t border-white/10 px-6 py-7 text-center md:px-10">
        <p className="text-[12px] text-white/35">
          ONEVOLT Energy GmbH · Borsigstraße 1 · 51381 Leverkusen ·{" "}
          <a href="/impressum/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/60">
            Impressum
          </a>{" "}
          ·{" "}
          <a href="/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/60">
            Datenschutz
          </a>
        </p>
      </footer>
    </div>
  );
}
